import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LabelService } from '../../services/label.service';
import { modifyCalendarEvent } from '../../models/calendar-model';
import moment from 'moment';
import { DatePipe } from '@angular/common';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-panel-modify-calendar-event',
  templateUrl: './panel-modify-calendar-event.component.html',
  styleUrls: ['./panel-modify-calendar-event.component.scss'],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  standalone: false,
})
export class PanelModifyCalendarEventComponent implements OnInit {
  @Input() appointmentDay: string = '';
  @Input() appointmentMonth: string = '';
  @Input() appointmentYear: string = '';
  @Input() appointmentStartHours: string = '';
  @Input() appointmentStartMinutes: string = '';
  @Input() appointmentEndHours: string = '';
  @Input() appointmentEndMinutes: string = '';
  @Input() appointmentId: string = '';
  @Input() appointmentTitle: string = '';
  @Input() isAppointmentAllDay: boolean = false;
  @Input() title: string = 'Modifica Evento';
  @Input() isAppointmentModifiable: boolean = true;
  appointmentLength: string = '';

  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<modifyCalendarEvent>();
  @Output() delete = new EventEmitter<string>();

  eventForm: FormGroup;

  isBtnClicked: boolean = false;

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>,
    private datePipe: DatePipe
  ) {
    this.eventForm = this.fb.group({
      title: ['', Validators.required],
      date: ['', Validators.required],
      startTime: [
        '',
        [
          Validators.required,
          Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d)$/),
        ],
      ],
      length: [
        '',
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      ],
    });

    this.dateAdapter.setLocale('it-IT');
  }

  ngOnInit(): void {
    this.populateFormFields();
  }

  populateFormFields(): void {
    const dateString = `${this.appointmentDay}/${this.appointmentMonth}/${this.appointmentYear}`;
    const startTime = `${this.appointmentStartHours}:${this.appointmentStartMinutes}`;
    const endTime = `${this.appointmentEndHours}:${this.appointmentEndMinutes}`;
    this.appointmentLength = this.calculateMinutesDifference(
      startTime,
      endTime
    );

    this.eventForm.patchValue({
      title: this.appointmentTitle,
      date: moment(dateString, 'DD/MM/YYYY'),
      startTime: startTime,
      endTime: endTime,
      length: this.appointmentLength,
      isAllDay: this.isAppointmentAllDay,
    });
  }

  calculateMinutesDifference(startTime: string, endTime: string): string {
    // Converti le stringhe in ore e minuti
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    // Calcola i minuti totali per ciascun orario
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    // Differenza in minuti
    return (endTotalMinutes - startTotalMinutes).toString();
  }

  calculateEndTime(startTime: string, lengthInMinutes: string): string {
    const timeParts = startTime.split(':'); // Dividi la stringa di orario in ore e minuti
    const duration = parseInt(lengthInMinutes); // Converti la stringa di durata in un numero intero
    const date = new Date(); // Crea un nuovo oggetto Date
    date.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0); // Imposta ore, minuti, secondi e millisecondi dal startTime

    date.setMinutes(date.getMinutes() + duration); // Aggiunge la lunghezza in minuti al tempo

    // Formatta l'orario di fine per restituirlo come stringa "HH:mm"
    const hours = date.getHours().toString().padStart(2, '0'); // Assicurati che le ore siano sempre due cifre
    const minutes = date.getMinutes().toString().padStart(2, '0'); // Assicurati che i minuti siano sempre due cifre

    return `${hours}:${minutes}`; // Restituisce il nuovo orario come stringa
  }

  onCancel() {
    this.cancel.emit();
  }

  onConfirm() {
    if (
      this.isBtnClicked ||
      this.eventForm.invalid ||
      !this.isAppointmentModifiable
    ) {
      return;
    }

    const formValues = this.eventForm.value;
    const dateString =
      this.datePipe.transform(formValues.date, 'dd/MM/yyyy') || '';
    const endTime = this.calculateEndTime(
      formValues.startTime,
      formValues.length
    );
    const output: modifyCalendarEvent = {
      id: this.appointmentId,
      title: formValues.title,
      date: dateString,
      start_time: formValues.isAllDay ? '00:00' : formValues.startTime,
      end_time: formValues.isAllDay ? '23:59' : endTime,
      isAllDay: formValues.isAllDay,
    };

    this.isBtnClicked = true;
    this.confirm.emit(output);
  }

  onDelete() {
    if (!this.isAppointmentModifiable) {
      return;
    }
    this.delete.emit(this.appointmentId);
  }
}
